import React, { FC } from "react";
import { BaseFeesWithCampaignLegacy } from "../PriceList/BaseFeesWithCampaignLegacy";
import { getDiscount } from "./Helpers/totalPriceCalculator";
import { formatPrice } from "../Helpers/priceHelper";
import classNames from "classnames";
import { PriceListFeesLegacy } from "../PriceList/PriceListFeesLegacy";
import { FeeWithDiscountTableLegacy } from "../PriceList/FeeWithDiscountTableLegacy";
import { ServicePricingSummary } from "../../Partials/PricingHighlight/ServicePricingSummary.csharp";

interface TreatmentPricingSummaryProps extends ServicePricingSummary {
  hideHeading?: boolean;
}

export const BookingPricingSummary: FC<TreatmentPricingSummaryProps> = ({
  serviceName,
  baseFees,
  listWithoutPrice,
  listWithoutPriceLabel,
  basePrice,
  eveningAddition,
  weekendHolidayAddition,
  isEvening,
  isWeekendOrHoliday,
  showWeekendEveningPrices,
  additionalFees,
  basePriceLabel,
  eveningPriceLabelInBooking,
  weekendPriceLabelInBooking,
  eveningPriceReservationLabel,
  endPriceLabel,
  additionalFeesLabel,
  hideHeading = false,
  isFriday,
  showWarningAboutHigherPrice,
  campaignPrice,
  highlightPriceRowOnHover,
  priceListFees,
}) => {
  const getEndPrice = () => {
    const baseFeesCost = baseFees.reduce(
      (prev, current) => prev + current.regularPrice,
      0
    );
    const price = getBasePrice();

    return formatPrice(
      price +
        baseFeesCost -
        getDiscount(isWeekendOrHoliday, isEvening, campaignPrice)
    );
  };

  const getBasePrice = () => {
    let price = basePrice;

    if (showWeekendEveningPrices) {
      if (isWeekendOrHoliday) price = basePrice + weekendHolidayAddition;
      else if (isEvening) price = basePrice + eveningAddition;
    }

    return price;
  };

  return (
    <>
      {!hideHeading && (
        <summary className="details__summary theme-inherit-light p-2 mb-0 pricing-details-summary">
          <span className="pe-1">{serviceName}</span>
          {listWithoutPrice && listWithoutPriceLabel && (
            <span className="text-nowrap italic">{listWithoutPriceLabel}</span>
          )}
          {!listWithoutPrice && (
            <span className="text-nowrap">{getEndPrice()}</span>
          )}
        </summary>
      )}
      <div className="py-2">
        {!listWithoutPrice && (
          <div>
            <table className="table">
              <tbody>
                <tr
                  className={classNames("pricing-details-row", {
                    "pricing-details-row-blue-hover": highlightPriceRowOnHover,
                  })}
                >
                  <th className="fw-normal">
                    {campaignPrice?.basePriceLabel ?? basePriceLabel}
                  </th>
                  <td className="text-end text-nowrap">
                    {formatPrice(basePrice)}
                  </td>
                </tr>
                {showWeekendEveningPrices && (
                  <>
                    {isEvening && !!eveningAddition && (
                      <tr
                        className={classNames("pricing-details-row", {
                          "pricing-details-row-blue-hover":
                            highlightPriceRowOnHover,
                        })}
                      >
                        <th className="fw-normal">
                          {eveningPriceLabelInBooking ?? basePriceLabel}
                        </th>
                        <td className="text-end text-nowrap">
                          {formatPrice(eveningAddition)}
                        </td>
                      </tr>
                    )}
                    {isWeekendOrHoliday && !!weekendHolidayAddition && (
                      <tr
                        className={classNames("pricing-details-row", {
                          "pricing-details-row-blue-hover":
                            highlightPriceRowOnHover,
                        })}
                      >
                        <th className="fw-normal">
                          {weekendPriceLabelInBooking ?? basePriceLabel}
                        </th>
                        <td className="text-end text-nowrap">
                          {formatPrice(weekendHolidayAddition)}
                        </td>
                      </tr>
                    )}
                  </>
                )}
                <BaseFeesWithCampaignLegacy
                  baseFees={baseFees}
                  campaign={campaignPrice}
                  isWeekendOrHoliday={isWeekendOrHoliday}
                  isEvening={isEvening}
                />
              </tbody>
            </table>
            <div className="pricing-details-summary fw-bold px-2">
              <span className="pe-1">{endPriceLabel}</span>
              <span className="text-nowrap">{getEndPrice()}</span>
            </div>
          </div>
        )}

        <PriceListFeesLegacy
          priceListFees={priceListFees}
          highlightPriceRowOnHover={highlightPriceRowOnHover}
          campaignPrice={campaignPrice}
        />

        {(!!additionalFees.length || showWarningAboutHigherPrice) && (
          <>
            {(!!additionalFees.length ||
              weekendHolidayAddition > 0 ||
              eveningAddition > 0) && (
              <>
                <div className="fw-bold mt-2 px-2">{additionalFeesLabel}</div>
                <table className="table">
                  <tbody>
                    {showWeekendEveningPrices &&
                      showWarningAboutHigherPrice &&
                      (isFriday && weekendHolidayAddition > 0 ? (
                        <tr
                          key={0}
                          className={classNames("pricing-details-row", {
                            "pricing-details-row-blue-hover":
                              highlightPriceRowOnHover,
                          })}
                        >
                          <th className="fw-normal">
                            {eveningPriceReservationLabel}
                          </th>
                          <td className="text-end">
                            {formatPrice(
                              weekendHolidayAddition -
                                (campaignPrice?.weekendHolidayAdditionDiscount ??
                                  0)
                            )}
                          </td>
                        </tr>
                      ) : (
                        eveningAddition > 0 && (
                          <tr
                            key={0}
                            className={classNames("pricing-details-row", {
                              "pricing-details-row-blue-hover":
                                highlightPriceRowOnHover,
                            })}
                          >
                            <th className="fw-normal">
                              {eveningPriceReservationLabel}
                            </th>
                            <td className="text-end">
                              {formatPrice(
                                eveningAddition -
                                  (campaignPrice?.eveningAdditionDiscount ?? 0)
                              )}
                            </td>
                          </tr>
                        )
                      ))}
                    <FeeWithDiscountTableLegacy
                      heading={additionalFeesLabel}
                      addHadingMargin
                      fees={additionalFees}
                      highlightPriceRowOnHover={highlightPriceRowOnHover}
                      getPrice={(fee) => fee.regularPrice}
                      getDiscount={(fee) => fee.regularDiscount}
                      showHeading={false}
                    />
                  </tbody>
                </table>
              </>
            )}
          </>
        )}
      </div>
    </>
  );
};
